// ---------------------------------------------------------------------------
// Validation schemas. Every form validates against one of these before any
// write (PLAN 11). They mirror the CHECK constraints in the migrations so a
// bad record is rejected on the device, not at the door with no signal.
// ---------------------------------------------------------------------------

import { z } from 'zod';
import {
  BEST_RETURN_TIMES,
  INACCESSIBLE_REASONS,
  LITERATURE,
  MAX_NOTE_LENGTH,
  SUPPORT_LEVELS,
  VISIT_OUTCOMES,
} from './enums.js';

export const MIN_PASSWORD_LENGTH = 12;

const uuid = z.string().uuid();

/** Empty strings from form inputs become null rather than ''. */
const nullableText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable();

export const visitDraftSchema = z
  .object({
    // Client-generated, which is what makes the push idempotent (PLAN 5.4).
    id: uuid,
    household_id: uuid,
    canvasser_id: uuid,
    visited_at: z.string().datetime({ offset: true }),
    outcome: z.enum(VISIT_OUTCOMES),
    // Why the door could not be worked, so the next canvasser knows before
    // walking it (docs/campaign-requirements.md § 3.4).
    inaccessible_reason: z.enum(INACCESSIBLE_REASONS).nullable().default(null),
    literature: z.enum(LITERATURE).default('none'),
    best_return_time: z.enum(BEST_RETURN_TIMES).nullable().default(null),
    sign_requested: z.boolean().default(false),
    volunteer_interest: z.boolean().default(false),
    notes: nullableText(MAX_NOTE_LENGTH).default(null),
  })
  .superRefine((v, ctx) => {
    // Mirrors constraint reason_requires_inaccessible.
    if (v.inaccessible_reason !== null && v.outcome !== 'inaccessible') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['inaccessible_reason'],
        message: 'A reason only applies when the door was inaccessible.',
      });
    }
    // Mirrors constraint return_time_requires_not_home.
    if (v.best_return_time !== null && v.outcome !== 'not_home') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['best_return_time'],
        message: 'Best time to return only applies to a not-home visit.',
      });
    }
  });

export type VisitDraft = z.infer<typeof visitDraftSchema>;
export type VisitDraftInput = z.input<typeof visitDraftSchema>;

/**
 * One person at the door, recorded independently of their housemates
 * (docs/campaign-requirements.md § 3.1). Support lives here rather than on the
 * visit: a single answer for a house holding a Republican and a Democrat was
 * always a lossy summary.
 */
export const personContactDraftSchema = z
  .object({
    // Client-generated, like the visit id, so the offline push stays
    // idempotent (PLAN 5.4).
    id: uuid,
    visit_id: uuid,
    person_id: uuid,
    canvasser_id: uuid,
    contacted_at: z.string().datetime({ offset: true }),
    spoke: z.boolean().default(false),
    support: z.enum(SUPPORT_LEVELS).nullable().default(null),
    sign_requested: z.boolean().default(false),
    notes: nullableText(MAX_NOTE_LENGTH).default(null),
  })
  .superRefine((v, ctx) => {
    // Mirrors constraint support_requires_spoke. Somebody nobody talked to
    // cannot have stated a position.
    if (v.support !== null && !v.spoke) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['support'],
        message: 'Record support only for someone who was actually spoken to.',
      });
    }
  });

export type PersonContactDraft = z.infer<typeof personContactDraftSchema>;
export type PersonContactDraftInput = z.input<typeof personContactDraftSchema>;

/**
 * A note about an address. Append-only: two canvassers writing at once produce
 * two notes rather than one overwriting the other
 * (docs/campaign-requirements.md § 3.6).
 */
export const houseNoteDraftSchema = z.object({
  // Client-generated, so a replayed offline push produces one note, not three.
  id: uuid,
  household_id: uuid,
  author_id: uuid,
  created_at: z.string().datetime({ offset: true }),
  // An empty note is not a note. Trimmed first, so whitespace is not a note
  // either.
  body: z.string().trim().min(1, 'Write something first.').max(MAX_NOTE_LENGTH),
});

export type HouseNoteDraft = z.infer<typeof houseNoteDraftSchema>;
export type HouseNoteDraftInput = z.input<typeof houseNoteDraftSchema>;

export const credentialsSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(1, 'Enter your password.'),
});
export type Credentials = z.infer<typeof credentialsSchema>;

/**
 * A new account: who, and as what. The organizer is then shown a password to
 * hand over (create_account(), 20260927000100).
 *
 * Not 'admin' -- create_invite(), which create_account() goes through, rejects
 * it, for the same reason the roster has no button for it
 * (docs/managing-volunteers.md § Roles).
 */
export const accountDraftSchema = z.object({
  email: z.string().trim().toLowerCase().email('That does not look like an email address.'),
  name: z.string().trim().min(1, 'A name is required — it is what the roster shows.'),
  role: z.enum(['canvasser', 'organizer']),
});
export type AccountDraft = z.infer<typeof accountDraftSchema>;

/**
 * The organizer's reset form. Both fields, because reset_password()
 * (20260927000100) refuses unless the name and email belong to the same
 * account -- a guard against resetting the wrong person over a typo.
 */
export const passwordResetSchema = z.object({
  name: z.string().trim().min(1, 'Enter their name as it appears on the roster.'),
  email: z.string().trim().toLowerCase().email('That does not look like an email address.'),
});
export type PasswordResetDraft = z.infer<typeof passwordResetSchema>;

/** Choosing a password after signing in with a temporary one. */
export const passwordChangeSchema = z
  .object({
    password: z
      .string()
      .min(MIN_PASSWORD_LENGTH, `At least ${MIN_PASSWORD_LENGTH} characters.`),
    confirm: z.string(),
  })
  .superRefine((v, ctx) => {
    if (v.password !== v.confirm) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['confirm'],
        message: 'The two passwords do not match.',
      });
    }
  });

export const doNotContactSchema = z.object({
  household_id: uuid,
  reason: nullableText(MAX_NOTE_LENGTH).default(null),
});

export const profileUpdateSchema = z.object({
  name: z.string().trim().min(1, 'Name is required.'),
  phone: nullableText(40).default(null),
});

/** GeoJSON polygon, as produced by the organizer's draw tool. */
export const polygonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z.array(z.array(z.tuple([z.number(), z.number()]))).min(1),
});

export const turfCreateSchema = z.object({
  name: z.string().trim().min(1, 'Turf needs a name.'),
  boundary: polygonSchema,
});
